/**
 * OTPInput — N-digit code input.
 *
 * Matches the Figma: glass-tinted boxes with a centered "-" placeholder
 * until filled, large monospace numerals, RTL-aware ordering, paste
 * support that fills the whole row.
 *
 * Behavior:
 *   - Auto-advance on digit entry
 *   - Backspace on empty cell moves focus to previous cell
 *   - Arrow keys navigate cells
 *   - Pasting a numeric string fills boxes left-to-right (logical, so
 *     RTL users still type the code in reading order)
 *
 * Controlled via `value` (string) + `onChange` (string).
 */

import { useEffect, useRef } from 'react'

export default function OTPInput({
  length = 6,
  value = '',
  onChange,
  onComplete,
  autoFocus = true,
  invalid = false,
  tone = 'default',
  inputMode = 'numeric',
  pattern = '[0-9]*',
  className = '',
}) {
  const refs = useRef([])

  useEffect(() => {
    if (autoFocus) refs.current[0]?.focus()
  }, [autoFocus])

  // Pad value out to `length` so each cell has a defined char
  const chars = String(value).split('').slice(0, length)
  while (chars.length < length) chars.push('')

  const setAt = (i, ch) => {
    const next = chars.slice()
    next[i] = ch
    const joined = next.join('').slice(0, length)
    onChange?.(joined)
    if (joined.length === length && !joined.includes('') && onComplete) {
      onComplete(joined)
    }
  }

  const handleChange = (i, e) => {
    const raw = e.target.value
    // Take only the last typed char (in case the input gets two)
    const ch = raw.slice(-1)
    if (ch && !/^\d$/.test(ch)) return
    setAt(i, ch)
    if (ch && i < length - 1) refs.current[i + 1]?.focus()
  }

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace') {
      if (chars[i]) {
        setAt(i, '')
      } else if (i > 0) {
        refs.current[i - 1]?.focus()
        setAt(i - 1, '')
      }
      e.preventDefault()
    } else if (e.key === 'ArrowLeft' && i > 0) {
      refs.current[i - 1]?.focus()
      e.preventDefault()
    } else if (e.key === 'ArrowRight' && i < length - 1) {
      refs.current[i + 1]?.focus()
      e.preventDefault()
    }
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length)
    if (!text) return
    onChange?.(text.padEnd(length, '').slice(0, length).trimEnd())
    const last = Math.min(text.length, length) - 1
    refs.current[last]?.focus()
    if (text.length === length && onComplete) onComplete(text)
  }

  const cellTone = tone === 'glass'
    ? 'bg-white/10 backdrop-blur border border-white/55 text-white placeholder:text-white/55 caret-white'
    : 'bg-surface border border-border text-ink-primary placeholder:text-ink-muted'
  const cellError = invalid
    ? (tone === 'glass'
        ? 'border-rose-300 focus:border-rose-300 focus:ring-rose-200/40'
        : 'border-danger focus:border-danger focus:ring-danger/30')
    : (tone === 'glass'
        ? 'focus:border-white focus:ring-white/30 focus:bg-white/20'
        : 'focus:border-brand focus:ring-brand/30')

  return (
    <div className={`flex items-center gap-2.5 sm:gap-3 ${className}`} dir="ltr">
      {chars.map((ch, i) => (
        <input
          key={i}
          ref={(el) => { refs.current[i] = el }}
          type="text"
          inputMode={inputMode}
          pattern={pattern}
          maxLength={1}
          value={ch}
          placeholder="-"
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={i === 0 ? handlePaste : undefined}
          onFocus={(e) => e.target.select()}
          aria-label={`Digit ${i + 1} of ${length}`}
          className={`
            w-12 h-12 sm:w-14 sm:h-14 rounded-xl text-center
            text-xl sm:text-2xl font-semibold
            transition-all focus:outline-none focus:ring-2
            ${tone === 'glass' ? 'shadow-[inset_0_1px_0_rgba(255,255,255,0.4)]' : ''}
            ${cellTone} ${cellError}
          `}
        />
      ))}
    </div>
  )
}
